import React, { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import Bugsnag from "@bugsnag/js";
import { faTrashCan, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Component imports:
import Modal from "../../../ui-elements/Modal/Modal";
import Button from "../../../ui-elements/Button/Button";
import Checkbox from "../../../ui-elements/Input/Formik/Checkbox";
import Alert, { AlertType } from "../../../ui-elements/Alert/Alert";
import Input from "../../../ui-elements/Input/Formik/Input";
import Link from "../../../ui-elements/Link/Link";
import Tooltip from "../../../ui-elements/Tooltip/Tooltip";
// Service imports:
import { formatDateToIcelandic, insertDash, isSSN, stripSSN } from "../../../services/utils";
import { GET_KELDAN_API_URL } from "../../../services/config";
// Type imports:
import { PepWatchListData, PepWatchResponse } from "../../../types/PepTypes";
import { IKeldanApiResponse } from "../../../types/KeldanTypes";
// Static data imports:
import { CountryList } from "../../../assets/staticData/CountryList";
import { ErrorMessages } from "../../../services/errorMessages";

interface IProps {
  show: boolean;
  close(): void;
  data?: PepWatchListData;
  setToastMessage(message: string): void;
  setToastType(type: AlertType): void;
  setToastShow(show: boolean): void;
  onSuccess(): void;
  initialPepChecked?: boolean;
}

interface IFormValues {
  pep: boolean;
  foreign: boolean;
  reference?: string;
}

// SubFormComponent moved outside of the main component
const SubFormComponent: React.FC<{
  values: IFormValues;
  data: PepWatchListData;
  close(): void;
  onDelete(): void;
}> = ({ values, data, close, onDelete }) => {
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const handleName = (first?: string, middle?: string, last?: string): string => {
    // Initialize an array to store the name parts
    const nameParts: string[] = [];

    // Add each name part to the array if it exists
    if (first) nameParts.push(first);
    if (middle) nameParts.push(middle);
    if (last) nameParts.push(last);

    // Join all existing name parts with a space and return
    return nameParts.join(" ");
  };

  const handleSSN = (ssn: string): string => {
    if (isSSN(ssn)) return insertDash(ssn);
    return "Ógild kennitala";
  };

  const countryName = useMemo(() => {
    const country = CountryList.find((country) => country.value === data.personCountryCode);
    return country ? country.label : "";
  }, [data.personCountryCode]);
  const hasRequiredFields = (data: any) => {
    const s = !!(data.personDateOfBirth && data.personCountryCode && data.personFirstName && data.personLastName);
    return s;
  };

  const handleDelete = async (id: string) => {
        try {
            const requestQuery = new URLSearchParams({
                id
            });
            const url = `${GET_KELDAN_API_URL()}/Pep/RemoveFromPepWatchList`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery,
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const responseBody: IKeldanApiResponse = await response.json();
                    if (responseBody.success) {
                      onDelete();
                      close();
                    }
                }
            } else {
                throw new Error(ErrorMessages.RequestFailed);
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
        }
    }

  return (
    <>
      <Form>
        <div className="KCL_pepEditWatchModal">
          <div className="left_wrapper">
            <span>
              <strong>Viðskiptavinur: </strong>
              {handleName(data.personFirstName, data.personMiddleName, data.personLastName)}
            </span>
            <span>
              <strong>Kennitala: </strong>
              {handleSSN(data.id.personId)}
            </span>
            {data.personDateOfBirth && (
              <span>
                <strong>Fæðingardagur: </strong>
                {formatDateToIcelandic(data.personDateOfBirth)}
              </span>
            )}
            {data.personCountryCode && (
              <span>
                <strong>Land: </strong>
                {countryName}
              </span>
            )}
          </div>

          <div className="right_wrapper">
            <div className="checkbox-wrapper">
              <Checkbox
                disabled={!isSSN(data.id.personId) || (!data.individualWatch && data.pepFrequency !== "never")}
                label={
                  !data.individualWatch && data.pepFrequency !== "never"
                    ? <>Vöktun á PEP lista{" "}<Tooltip noUnderline text={<FontAwesomeIcon icon={faInfoCircle} className="pep-edit-info-icon" />} tooltip={<p>Vaktaður sem aðili tengdur lögaðila</p>} /></>
                    : "Vöktun á PEP lista"
                }
                name="pep"
                id="pep"
                value={values.pep.toString()}
              />
              <Checkbox
                label={
                  !data.individualWatch && data.sanctionsFrequency !== "never"
                    ? <>Vöktun á alþjóðlegum válistum{" "}<Tooltip noUnderline text={<FontAwesomeIcon icon={faInfoCircle} className="pep-edit-info-icon" />} tooltip={<p>Vaktaður sem aðili tengdur lögaðila</p>} /></>
                    : "Vöktun á alþjóðlegum válistum"
                }
                name="foreign"
                id="foreign"
                disabled={!hasRequiredFields(data) || (!data.individualWatch && data.sanctionsFrequency !== "never")}
                value={values.foreign.toString()}
              />
            </div>
            <div>
              <Input
                label="Athugasemd"
                name="reference"
                id="reference"
                value={values.reference || ""}
              />
            </div>
          </div>
        </div>
        {/* <div className="form__body">
          <div className="form__row">
            <div className="form__col">
              <span>
                <strong>Viðskiptavinur: </strong>
                {handleName(data.personFirstName, data.personMiddleName, data.personLastName)}
              </span>
            </div>
            <div className="form__col">
              <Checkbox
                disabled={!isSSN(data.id.personId)}
                label="Vöktun á PEP lista"
                name="pep"
                id="pep"
                value={values.pep.toString()}
              />
            </div>
          </div>
          <div className="form__row">
            <div className="form__col">
              <span>
                <strong>Kennitala: </strong>
                {handleSSN(data.id.personId)}
              </span>
            </div>
            <div className="form__col">
              <Checkbox
                label="Vöktun á alþjóðlegum válistum"
                name="foreign"
                id="foreign"
                disabled={!hasRequiredFields(data)}
                value={values.foreign.toString()}
              />
            </div>
          </div>
          {data.personDateOfBirth && (
            <div className="form__row">
              <div className="form__col">
                <span>
                  <strong>Fæðingardagur: </strong>
                  {formatDateToIcelandic(data.personDateOfBirth)}
                </span>
              </div>
            </div>
          )}
          {data.personCountryCode && (
            <div className="form__row">
              <div className="form__col">
                <span>
                  <strong>Land: </strong>
                  {countryName}
                </span>
              </div>
            </div>
          )}
        </div> */}
        <div className="form__actions">
          <Button type="button" buttonType="secondary" size="lg" onClick={() => close()}>
            Hætta við
          </Button>
          <Button type="submit" buttonType="primary" size="lg">
            Staðfesta
          </Button>
          <Button type="button" buttonType="secondary" size="lg" onClick={() => setConfirmDeleteModalOpen(true)} icon={faTrashCan}>
            Eyða af lista
          </Button>
        </div>
      </Form>
      <Modal
        className="confirm-delete-modal"
        show={confirmDeleteModalOpen}
        onHide={() => setConfirmDeleteModalOpen(false)}
        header="Eyða viðskiptavini"
        body={
          <div className="alertBody">
            <Alert
              type="alert"
              headText="Þú ert að fara eyða viðskiptavini úr vöktunarkerfinu."
            />
            <div className="infoSection">
              <p className="paragraph">
                Allar tengdar upplýsingar verða <span className="paragraph--bold">endanlega fjarlægðar</span> og ekki verður unnt að endurheimta þær.
              </p>
              <p className="paragraph">
                Viltu staðfesta?
              </p>
            </div>
            <div className="actions">
              <Button onClick={() => setConfirmDeleteModalOpen(false)} buttonType="secondary" size="lg">Hætta við</Button>
              <Button onClick={() => handleDelete(data.id.personId)} buttonType="primary" size="lg">Staðfesta</Button>
            </div>
          </div>
        }
      />
    </>
  );
};

const PepEditWatchModal: React.FC<IProps> = ({
  show,
  close,
  data,
  setToastMessage,
  setToastType,
  setToastShow,
  onSuccess,
  initialPepChecked,
}) => {
  // All hooks must be called unconditionally at the top level
  const subFormComponent = useMemo(() => {
    // We'll only use this component if data exists
    // The function signature must match what Formik expects
    return (props: { values: IFormValues }) => {
      // Only render the actual form component if data exists
      if (!data) return null;

      return <SubFormComponent values={props.values} data={data} close={close} onDelete={onSuccess} />;
    };
  }, [close, data]);

  // Handle the case where data is undefined
  // We don't return early anymore, but conditionally render based on data
  const modalBody = () => {
    if (!data) return null;

    return (
      <div>
        <h3>Breyta vöktun á viðskiptavini</h3>
        <p className="paragraph--bold">
          Verð eru samkvæmt{" "}
          <Link linkSize="18" url="/Verdskra#Gagnaleit">
            verðskrá Keldunnar
          </Link>{" "}
          eða samningi hverju sinni.
        </p>
        <Formik
          initialValues={{
            pep: initialPepChecked || data.pepFrequency !== "never",
            foreign: data.sanctionsFrequency !== "never",
            reference: data.reference || "",
          }}
          onSubmit={handleSubmit}
          component={subFormComponent}
        />
      </div>
    );
  };

  const handleSubmit = async (values: IFormValues) => {
    // Only proceed if data exists
    if (!data) return;

    try {
      const requestQuery = new URLSearchParams({
        PersonId: stripSSN(data.id.personId) ?? "",
        FirstName: data.personFirstName,
        LastName: data.personLastName,
        PepFrequency: values.pep ? "daily" : "never",
        SanctionsFrequency: values.foreign ? "daily" : "never",
        reference: values.reference || "",
      });
      const url = `${GET_KELDAN_API_URL()}/Pep/UpdatePepWatchList`;
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: requestQuery,
      });
      const responseBody: IKeldanApiResponse<PepWatchResponse<PepWatchListData[]>> = await response.json();
      if (responseBody.success) {
        onSuccess();
        setToastMessage("Vöktun á viðskiptavini breytt");
        setToastType("success");
        setToastShow(true);
        close();
      } else {
        setToastMessage("Vöktun á viðskiptavini ekki breytt");
        setToastType("alert");
        setToastShow(true);
      }
    } catch (e) {
      if (e instanceof Error) Bugsnag.notify(e);
      setToastMessage("Vöktun á viðskiptavini ekki breytt");
      setToastType("error");
      setToastShow(true);
    }
  };

  // Always return the Modal, even if data is undefined
  return <Modal className="pep-watch-modal" show={show} onHide={close} hasCloseButton body={modalBody()} />;
};

export default PepEditWatchModal;
